import { fileSchema, REQUIRED_MESSAGE, requiredString } from '@wyz/utils';
import { z } from 'zod';
import {
  associatedCriteriaFormSchema,
  associatedCriteriaSchema,
} from '~/common/schemas';

const INVALID_END_DATE_MESSAGE = 'app.common.validations.end_date_lower_start';

export const baseOdrOperation = z.object({
  id: z.string(),
  name: requiredString,
  brands: z.array(z.object({ id: requiredString })).min(1, REQUIRED_MESSAGE),
  startDate: z.date({ error: REQUIRED_MESSAGE }),
  endDate: z.date({ error: REQUIRED_MESSAGE }),
  criteria: z.array(associatedCriteriaSchema),
  // Derived server-side from startDate/endDate — read-only, never part of the
  // create payload/form (see the .omit() below).
  computedStatus: z.enum(['future', 'active', 'ended']),
  // Persisted server-side, unlike computedStatus. Only 'archived' matters to the UI:
  // it is set by the archiving job and freezes the operation.
  status: z.enum(['enabled', 'disabled', 'archived']),
  // Activation for the PDV the list is scoped to (the `customerId` query param).
  // 'enabled' whenever the request targets no single PDV; an operation with no
  // entry for that PDV is enabled, so only an explicit 'disabled' deactivates it.
  currentCustomerStatus: z.enum(['enabled', 'disabled']).nullish(),
  filePath: requiredString,
  fileName: requiredString,
});

export const odrOperationPayload = baseOdrOperation
  .omit({
    id: true,
    startDate: true,
    endDate: true,
    computedStatus: true,
    status: true,
    currentCustomerStatus: true,
    filePath: true,
    fileName: true,
  })
  .extend({
    startDate: z.string(),
    endDate: z.string(),
    file: fileSchema,
  });

const odrOperationFormShape = baseOdrOperation
  .omit({
    id: true,
    brands: true,
    criteria: true,
    computedStatus: true,
    status: true,
    currentCustomerStatus: true,
    filePath: true,
    fileName: true,
  })
  .extend({
    brands: z.array(z.string()).min(1, REQUIRED_MESSAGE),
    criteria: z
      .array(associatedCriteriaFormSchema)
      .min(1, { message: REQUIRED_MESSAGE }),
  });

function refineDatesOrder<T extends { startDate: Date; endDate: Date }>(
  formData: T,
  ctx: z.RefinementCtx,
) {
  if (formData.startDate > formData.endDate) {
    ctx.addIssue({
      code: 'custom',
      message: INVALID_END_DATE_MESSAGE,
      path: ['endDate'],
      fatal: true,
    });
  }
}

export const odrOperationForm = odrOperationFormShape
  .extend({ file: fileSchema })
  .superRefine(refineDatesOrder);

export const odrOperationUpdatePayload = odrOperationPayload.extend({
  // A new visual is only sent when the user replaces it — the existing one
  // stays valid otherwise, so unlike create it isn't required here.
  file: fileSchema.optional(),
});

export const odrOperationEditForm = odrOperationFormShape
  .extend({ file: fileSchema.optional() })
  .superRefine(refineDatesOrder);
